import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  KOLKATA_HIGHLIGHTS,
  KOLKATA_PAGES,
  KOLKATA_STEPS,
  KOLKATA_WHY_CHOOSE,
  KolkataPage,
} from './kolkata-pages.data';
import { KOLKATA_SECTION_IMAGES, KOLKATA_SERVICE_TYPE, KOLKATA_TREATMENT_CARDS } from './kolkata-treatment-cards.data';
import { KolkataCarouselCard, KolkataTreatmentCarousel } from './kolkata-treatment-carousel';

const SITE = 'https://www.redmonkwellness.com';

// Verified Kolkata contact details
const KOLKATA_TEL = 'tel:+916262030303';
const KOLKATA_WHATSAPP = 'https://wa.me/916262030303';
const KOLKATA_FLOAT_WHATSAPP =
  'https://api.whatsapp.com/send?phone=916262030303&text=I%20am%20interested%20!%20Please%20share%20me%20more%20details%20about%20Red%20Monk%20Welness%20!';

// Mumbai values of the floating buttons in index.html, restored when leaving a Kolkata page
const MUMBAI_FLOAT_WHATSAPP =
  'https://api.whatsapp.com/send?phone=916262662662&text=I%20am%20interested%20!%20Please%20share%20me%20more%20details%20about%20Red%20Monk%20Welness%20!';
const MUMBAI_FLOAT_TEL = 'tel:+916262662662';

@Component({
  selector: 'app-kolkata-service',
  standalone: true,
  imports: [CommonModule, RouterLink, KolkataTreatmentCarousel],
  templateUrl: './kolkata-service.html',
  styleUrl: './kolkata-service.css',
})
export class KolkataService implements OnInit, OnDestroy {
  page!: KolkataPage;
  /** Images for the About and Kolkata blocks. */
  get sectionImages() {
    return KOLKATA_SECTION_IMAGES[this.page.id];
  }

  /** The approved treatment groups as carousel cards (image added where the project has one). */
  groupCards: { title: string; cards: KolkataCarouselCard[] }[] = [];

  readonly highlights = KOLKATA_HIGHLIGHTS;
  readonly steps = KOLKATA_STEPS;
  readonly why = KOLKATA_WHY_CHOOSE;

  readonly whatsapp = KOLKATA_WHATSAPP;

  /** Site-wide LocalBusiness blocks from index.html, detached while a Kolkata page is shown. */
  private detachedSchemas: HTMLScriptElement[] = [];

  constructor(
    private route: ActivatedRoute,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    const id = this.route.snapshot.data['kolkataId'];
    this.page = KOLKATA_PAGES.find((p) => p.id === id) ?? KOLKATA_PAGES[0];

    const images = new Map((KOLKATA_TREATMENT_CARDS[this.page.id] ?? []).map((c) => [c.name, c]));
    this.groupCards = this.page.groups.map((g) => ({
      title: g.title,
      cards: g.items.map((t) => {
        const c = images.get(t.name);
        return { name: t.name, desc: t.desc, img: c?.img, alt: c ? `${c.alt} in Kolkata` : undefined };
      }),
    }));
  }

  ngOnInit(): void {
    this.setSocialMeta();
    this.setJsonLd();
    this.setFloatingButtons(true);
  }

  ngOnDestroy(): void {
    this.removeJsonLd();
    this.restoreSiteBusinessSchemas();
    this.setFloatingButtons(false);
  }

  get canonicalUrl(): string {
    return SITE + this.page.slug + '/';
  }

  /** Floating call / WhatsApp buttons live in index.html (outside Angular). */
  private setFloatingButtons(kolkata: boolean): void {
    const wa = this.document.querySelector('.floating-buttons a.whatsapp');
    const call = this.document.querySelector('.floating-buttons a.call');
    wa?.setAttribute('href', kolkata ? KOLKATA_FLOAT_WHATSAPP : MUMBAI_FLOAT_WHATSAPP);
    call?.setAttribute('href', kolkata ? KOLKATA_TEL : MUMBAI_FLOAT_TEL);
  }

  private setSocialMeta(): void {
    const p = this.page;
    // AppTitleStrategy only updates the canonical in the browser; set it here so SSR output is correct too
    let canonical = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', this.canonicalUrl);

    this.meta.updateTag({ property: 'og:site_name', content: 'Red Monk Wellness' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_IN' });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    if (p.ogImage) {
      const image = `${SITE}/${p.ogImage}`;
      this.meta.updateTag({ property: 'og:image', content: image });
      this.meta.updateTag({ name: 'twitter:image', content: image });
      if (p.banner?.alt) {
        this.meta.updateTag({ property: 'og:image:alt', content: p.banner.alt });
        this.meta.updateTag({ name: 'twitter:image:alt', content: p.banner.alt });
      }
    }
  }

  private setJsonLd(): void {
    this.removeJsonLd();
    this.detachSiteBusinessSchemas();
    const p = this.page;
    const url = this.canonicalUrl;

    this.addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'MedicalClinic',
      '@id': `${url}#clinic`,
      name: 'Red Monk Wellness - Kolkata',
      url,
      telephone: '+91-62-62-03-03-03',
      email: 'info@redmonkwellness.com',
      image: `${SITE}/assets/images/redmonkwellness-logo.png`,
      address: {
        '@type': 'PostalAddress',
        streetAddress:
          'Crescent Tower, Lala Lajpat Rai Sarani, 229, Acharya Jagadish Chandra Bose Rd, Sreepally, Elgin',
        addressLocality: 'Kolkata',
        addressRegion: 'West Bengal',
        postalCode: '700020',
        addressCountry: 'IN',
      },
      parentOrganization: { '@type': 'Organization', name: 'Red Monk Wellness', url: `${SITE}/` },
    });

    this.addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${url}#service`,
      name: p.title.split(' | ')[0],
      serviceType: KOLKATA_SERVICE_TYPE[p.id],
      description: p.description,
      url,
      provider: { '@id': `${url}#clinic` },
      areaServed: { '@type': 'City', name: 'Kolkata' },
      ...(p.ogImage ? { image: `${SITE}/${p.ogImage}` } : {}),
    });

    this.addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/Home/` },
        { '@type': 'ListItem', position: 2, name: 'Kolkata', item: `${SITE}/Contactus/` },
        { '@type': 'ListItem', position: 3, name: p.navName, item: url },
      ],
    });

    this.addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: p.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  private addJsonLd(schema: object): void {
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-schema', 'kolkata-service');
    script.textContent = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  /** The page's own MedicalClinic block is the single business entity; drop index.html's Mumbai/Kolkata LocalBusiness blocks. */
  private detachSiteBusinessSchemas(): void {
    this.document
      .querySelectorAll('script[type="application/ld+json"]:not([data-seo-schema])')
      .forEach((s) => {
        try {
          if (JSON.parse(s.textContent ?? '')['@type'] === 'LocalBusiness') {
            this.detachedSchemas.push(s as HTMLScriptElement);
            s.remove();
          }
        } catch {
          /* leave unparseable blocks untouched */
        }
      });
  }

  /** Browser only, so client-side navigation to other pages gets the site-wide blocks back. */
  private restoreSiteBusinessSchemas(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.detachedSchemas.forEach((s) => this.document.head.appendChild(s));
    }
    this.detachedSchemas = [];
  }

  private removeJsonLd(): void {
    this.document
      .querySelectorAll('script[data-seo-schema="kolkata-service"]')
      .forEach((s) => s.remove());
  }
}
